'use client'

import { createSignDocument } from '@/lib/api/administrative/sign-api'
import { useState } from 'react'
import { MdArrowBack, MdDraw, MdUploadFile } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

export default function SignDocumentFormPage() {
    const navigate = useNavigate()

    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [description, setDescription] = useState('')
    const [loading, setLoading] = useState(false)

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault()

        if (!selectedFile) {
            toast.error('Selecione um arquivo PDF')
            return
        }

        setLoading(true)
        try {
            const data = {
                description: description.trim() || undefined,
            }

            const formData = new FormData()
            formData.append('file', selectedFile)
            formData.append('data', JSON.stringify(data))

            const response = await createSignDocument(formData)
            if (response.success && response.result) {
                toast.success(
                    'Documento criado! Adicione os signatários para enviar.',
                )
                navigate(`/commerce/subscriptions/${response.result.id}`)
            } else {
                toast.error(response.error || 'Erro ao criar documento')
            }
        } catch {
            toast.error('Erro ao criar documento')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="flex flex-col gap-6 max-w-2xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate('/commerce/subscriptions')}
                    className="btn btn-ghost btn-sm"
                >
                    <MdArrowBack className="text-lg" />
                </button>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary via-secondary/90 to-secondary/70 flex items-center justify-center shadow-lg shadow-secondary/20">
                    <MdDraw className="text-3xl text-white" />
                </div>
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-base-content">
                        Novo Documento
                    </h1>
                    <p className="text-sm text-base-content/60 mt-1">
                        Faça upload de um PDF para enviar para assinatura
                        eletrônica
                    </p>
                </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
                <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6 space-y-5">
                    {/* File upload */}
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text font-medium">
                                Arquivo PDF
                            </span>
                        </label>
                        <div
                            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                                selectedFile
                                    ? 'border-secondary/50 bg-secondary/5'
                                    : 'border-base-300 hover:border-secondary/30'
                            }`}
                        >
                            {selectedFile ? (
                                <div className="flex flex-col items-center gap-2">
                                    <MdUploadFile className="text-4xl text-secondary" />
                                    <p className="font-medium text-base-content">
                                        {selectedFile.name}
                                    </p>
                                    <p className="text-xs text-base-content/50">
                                        {(
                                            selectedFile.size /
                                            (1024 * 1024)
                                        ).toFixed(2)}{' '}
                                        MB
                                    </p>
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-xs mt-1"
                                        onClick={() => setSelectedFile(null)}
                                    >
                                        Remover
                                    </button>
                                </div>
                            ) : (
                                <label className="cursor-pointer flex flex-col items-center gap-2">
                                    <MdUploadFile className="text-4xl text-base-content/30" />
                                    <p className="text-base-content/60">
                                        Clique para selecionar ou arraste o
                                        arquivo
                                    </p>
                                    <input
                                        type="file"
                                        accept=".pdf,application/pdf"
                                        className="hidden"
                                        onChange={e => {
                                            const file = e.target.files?.[0]
                                            if (file) {
                                                if (
                                                    file.type !==
                                                    'application/pdf'
                                                ) {
                                                    toast.error(
                                                        'Apenas arquivos PDF são permitidos',
                                                    )
                                                    return
                                                }
                                                if (
                                                    file.size >
                                                    10 * 1024 * 1024
                                                ) {
                                                    toast.error(
                                                        'Arquivo deve ter no máximo 10MB',
                                                    )
                                                    return
                                                }
                                                setSelectedFile(file)
                                            }
                                        }}
                                    />
                                </label>
                            )}
                        </div>
                        <label className="label">
                            <span className="label-text-alt text-base-content/50">
                                Tamanho máximo: 10MB. Apenas PDF.
                            </span>
                        </label>
                    </div>

                    {/* Description */}
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text font-medium">
                                Descrição
                            </span>
                        </label>
                        <input
                            type="text"
                            className="input input-bordered w-full"
                            placeholder="Ex: Contrato de prestação de serviços"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            maxLength={200}
                        />
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 mt-6">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/commerce/subscriptions')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={loading || !selectedFile}
                    >
                        {loading ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdUploadFile className="text-lg" />
                        )}
                        Criar Documento
                    </button>
                </div>
            </form>
        </div>
    )
}
